<template>
  <div>
    <!-- Filters: the month reloads; the rest filter the loaded rows -->
    <div class="hr-bar">
      <div class="hr-field">
        <label>Month</label>
        <div class="hr-week-picker">
          <button class="hr-btn hr-btn-sm" @click="shiftMonth(-1)">
            <b-icon icon="chevron-left" font-scale="0.85" />
          </button>
          <select v-model.number="monthIndex" class="hr-select">
            <option v-for="(m, i) in monthNames" :key="i" :value="i">{{
              m
            }}</option>
          </select>
          <select v-model.number="year" class="hr-select">
            <option v-for="y in yearOptions" :key="y" :value="y">{{
              y
            }}</option>
          </select>
          <button class="hr-btn hr-btn-sm" @click="shiftMonth(1)">
            <b-icon icon="chevron-right" font-scale="0.85" />
          </button>
        </div>
      </div>
      <div v-for="f in FILTERS" :key="f.key" class="hr-field">
        <label>{{ f.label }}</label>
        <!-- multi-select: nothing picked = All -->
        <multiselect
          v-model="filter[f.key]"
          :options="options(f.key)"
          multiple
          :close-on-select="false"
          :clear-on-select="false"
          placeholder="All"
          select-label=""
          deselect-label=""
          selected-label="✓"
          :class="['cu-multi', `cu-f-${f.key}`]"
          :aria-label="f.label"
        >
          <template slot="selection" slot-scope="{ values, isOpen }">
            <span v-if="values.length && !isOpen" class="multiselect__single">{{
              values.length === 1 ? values[0] : `${values.length} selected`
            }}</span>
          </template>
        </multiselect>
      </div>
    </div>

    <div class="hr-hint" style="margin-bottom: 10px">
      Logged = billable hours whose timer stopped in the month. Subscription =
      the customer's current monthly hours. Customers without subscription hours
      show "—" and are left out of the utilization %.
    </div>

    <div
      v-if="isLoading"
      class="hr-card"
      style="padding: 40px; text-align: center"
    >
      <b-spinner variant="primary" />
      <p class="mt-3" style="color: #64748b">Loading contract utilization…</p>
    </div>
    <div v-if="error && !isLoading" class="alert alert-danger">
      {{ error }}
    </div>

    <!-- v-show, not v-if: unmounting while Google Charts loads makes it draw into nothing -->
    <div v-show="!isLoading && !error">
      <div class="hr-kpis">
        <div class="hr-card hr-kpi">
          <div class="k-top">
            <b-icon icon="speedometer2" font-scale="0.9" /> Utilization
          </div>
          <div class="k-val" :style="{ color: utilColor(totals.util) }">
            {{ fmtPct(totals.util) }}
          </div>
          <div class="cu-meter" aria-hidden="true">
            <span
              :style="{
                width: `${Math.min(100, totals.util || 0)}%`,
                background: utilColor(totals.util)
              }"
            />
          </div>
          <div class="k-sub">logged vs subscription · 100% = on plan</div>
        </div>
        <div class="hr-card hr-kpi">
          <div class="k-top">
            <b-icon icon="clock" font-scale="0.9" /> Logged
          </div>
          <div class="k-val">{{ fmtH(totals.logged) }}h</div>
          <div class="k-sub">{{ filtered.length }} customers</div>
        </div>
        <div class="hr-card hr-kpi">
          <div class="k-top">
            <b-icon icon="credit-card" font-scale="0.9" /> Subscription
          </div>
          <div class="k-val">{{ fmtH(totals.hours) }}h</div>
          <div class="k-sub">contracted this month</div>
        </div>
        <div class="hr-card hr-kpi">
          <div class="k-top">
            <b-icon icon="plus-slash-minus" font-scale="0.9" /> Δ
          </div>
          <div class="k-val" :style="{ color: totals.delta > 0 ? ROSE : '' }">
            {{ fmtDelta(totals.delta) }}h
          </div>
          <div class="k-sub">logged − subscription</div>
        </div>
      </div>

      <div v-if="companies.length" class="hr-card cu-chart">
        <div class="cu-chart-head">
          <div class="hr-section-title">
            Logged vs subscription hours · by company
          </div>
          <div class="hr-legend">
            <span><i :style="{ background: COLORS[0] }" />Logged</span>
            <span><i :style="{ background: COLORS[1] }" />Subscription</span>
          </div>
        </div>
        <GChart type="BarChart" :data="chartData" :options="chartOptions" />
      </div>

      <div class="hr-card">
        <table class="hr-table">
          <thead>
            <tr>
              <th>Company / customer</th>
              <th class="num">Logged</th>
              <th class="num">Subscription</th>
              <th class="num">Δ</th>
              <th class="num">Utilization</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="!companies.length">
              <td colspan="5">
                <div class="hr-empty">No customers match these filters.</div>
              </td>
            </tr>
            <template v-for="c in companies">
              <tr :key="c.name" class="cu-company">
                <td>{{ c.name }}</td>
                <td class="num">{{ fmtH(c.logged) }}</td>
                <td class="num">{{ fmtH(c.hours) }}</td>
                <td class="num">{{ fmtDelta(c.delta) }}</td>
                <td class="num" :style="{ color: utilColor(c.util) }">
                  {{ fmtPct(c.util) }}
                </td>
              </tr>
              <tr v-for="r in c.rows" :key="r.id" class="cu-customer">
                <td>{{ r.contact }}</td>
                <td class="num">{{ fmtH(r.hoursLogged) }}</td>
                <td class="num">{{ fmtH(r.hours) }}</td>
                <td class="num">{{ fmtDelta(r.hoursLogged - r.hours) }}</td>
                <td class="num" :style="{ color: utilColor(util(r)) }">
                  {{ fmtPct(util(r)) }}
                </td>
              </tr>
            </template>
          </tbody>
          <tfoot v-if="companies.length">
            <tr class="cu-company">
              <td>Total</td>
              <td class="num">{{ fmtH(totals.logged) }}</td>
              <td class="num">{{ fmtH(totals.hours) }}</td>
              <td class="num">{{ fmtDelta(totals.delta) }}</td>
              <td class="num" :style="{ color: utilColor(totals.util) }">
                {{ fmtPct(totals.util) }}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import { getReportSubscriptions } from "@/apis/customers";
import Multiselect from "vue-multiselect";

const FILTERS = [
  { key: "company", label: "Company" },
  { key: "contact", label: "Customer" },
  { key: "profile", label: "Profile" },
  { key: "membership", label: "Membership" }
];
const COLORS = ["#56a5b3", "#a78bfa"]; // logged, subscription
const ROSE = "#e11d48";

// Logged ÷ subscription for rows that have subscription hours; null when none do (shown as "—").
const sumUp = rows => {
  const logged = rows.reduce((a, r) => a + r.hoursLogged, 0);
  const hours = rows.reduce((a, r) => a + r.hours, 0);
  const onPlan = rows.filter(r => r.hours > 0);
  const planned = onPlan.reduce((a, r) => a + r.hours, 0);
  const loggedOnPlan = onPlan.reduce((a, r) => a + r.hoursLogged, 0);
  return {
    logged,
    hours,
    delta: logged - hours,
    util: planned ? (loggedOnPlan / planned) * 100 : null
  };
};

/* Reports > Contract Utilization (admins): per customer, the month's billable logged hours
   against their monthly subscription hours (GET reports/subscriptions/?month=YYYY-MM). */
export default {
  name: "ContractUtilization",
  components: { Multiselect },
  data() {
    return {
      FILTERS,
      COLORS,
      ROSE,
      monthCursor: moment().startOf("month"),
      filter: { company: [], contact: [], profile: [], membership: [] }, // empty = all
      rows: [],
      isLoading: false,
      error: ""
    };
  },
  computed: {
    monthNames: () => moment.monthsShort(),
    yearOptions() {
      const now = moment().year();
      return Array.from({ length: 6 }, (_, i) => now - 5 + i);
    },
    monthIndex: {
      get() {
        return this.monthCursor.month();
      },
      set(m) {
        this.setMonth(this.monthCursor.clone().month(m));
      }
    },
    year: {
      get() {
        return this.monthCursor.year();
      },
      set(y) {
        this.setMonth(this.monthCursor.clone().year(y));
      }
    },
    filtered() {
      return this.rows.filter(r =>
        FILTERS.every(
          ({ key }) =>
            !this.filter[key].length || this.filter[key].includes(r[key])
        )
      );
    },
    companies() {
      const groups = {};
      this.filtered.forEach(r =>
        (groups[r.company] = groups[r.company] || []).push(r)
      );
      return Object.keys(groups)
        .sort((a, b) => a.localeCompare(b))
        .map(name => ({
          name,
          rows: groups[name].sort((a, b) => a.contact.localeCompare(b.contact)),
          ...sumUp(groups[name])
        }));
    },
    totals() {
      return sumUp(this.filtered);
    },
    chartData() {
      return [
        ["Company", "Logged", "Subscription"],
        ...this.companies.map(c => [c.name, +c.logged.toFixed(1), c.hours])
      ];
    },
    chartOptions() {
      return {
        backgroundColor: "transparent",
        // one row per company, so the chart grows instead of squashing labels
        height: Math.max(160, this.companies.length * 34 + 40),
        chartArea: { left: 160, right: 16, top: 8, bottom: 32 },
        legend: "none",
        colors: COLORS,
        bar: { groupWidth: "70%" },
        hAxis: {
          minValue: 0,
          textStyle: { fontSize: 11, color: "#94a3b8" },
          gridlines: { color: "#eef2f6" }
        },
        vAxis: { textStyle: { fontSize: 12, color: "#334155" } }
      };
    }
  },
  mounted() {
    this.load();
  },
  methods: {
    util(r) {
      return r.hours > 0 ? (r.hoursLogged / r.hours) * 100 : null;
    },
    utilColor(u) {
      if (u === null || u === undefined) return "#64748b";
      return u > 100 ? ROSE : "#0d9488";
    },
    fmtH: h => (h || 0).toFixed(1),
    fmtPct: u => (u === null || u === undefined ? "—" : `${u.toFixed(1)}%`),
    fmtDelta: d => `${d > 0 ? "+" : ""}${(d || 0).toFixed(1)}`,
    options(key) {
      return [...new Set(this.rows.map(r => r[key]))].sort();
    },
    shiftMonth(step) {
      this.setMonth(this.monthCursor.clone().add(step, "month"));
    },
    setMonth(m) {
      this.monthCursor = m.startOf("month");
      this.load();
    },
    async load() {
      this.isLoading = true;
      this.error = "";
      try {
        const res = await getReportSubscriptions({
          month: this.monthCursor.format("YYYY-MM"),
          status: "active"
        });
        this.rows = res.data.results;
      } catch (e) {
        this.rows = [];
        this.error =
          (e.response && e.response.data && e.response.data.detail) ||
          "Could not load contract utilization.";
      } finally {
        this.isLoading = false;
      }
    }
  }
};
</script>

<style scoped>
.cu-multi {
  min-width: 190px;
  min-height: unset;
  font-size: 13px;
}
.cu-f-company,
.cu-f-contact {
  min-width: 240px;
}
.cu-multi >>> .multiselect__tags {
  min-height: unset;
  padding: 7px 40px 0 11px;
  border: 1px solid #e2e8f0;
  border-radius: 9px;
}
.cu-multi >>> .multiselect__select {
  height: 34px;
}
.cu-multi >>> .multiselect__placeholder,
.cu-multi >>> .multiselect__single {
  margin-bottom: 6px;
  padding-top: 0;
  top: 0;
  font-size: 13px;
  color: #334155;
}
.cu-chart {
  margin-bottom: 18px;
  padding: 16px 18px 8px;
}
.cu-chart .hr-section-title {
  padding: 0;
}
.cu-chart-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
}
.cu-meter {
  height: 6px;
  border-radius: 3px;
  background: #eef2f6;
  margin: 8px 0 6px;
  overflow: hidden;
}
.cu-meter span {
  display: block;
  height: 100%;
}
.cu-company td {
  font-weight: 700;
  background: #f8fafc;
}
.cu-customer td:first-child {
  padding-left: 28px;
}
.hr-table .num {
  text-align: right;
}
</style>
